import type { Module } from 'vuex';
import { IUser } from '../../entities/user-entity';

export const namespace = 'user';

export const PASS_REQUEST_TIMESTAMP = 'pass_request_timestamp';
export const USER_JWT_TOKEN = 'authToken';
export const USER_SESSION_TOKEN = 'sessionToken';

const formatPhoneNumber = (phoneNumber: string): string => {
	const phoneFormatPattern = new RegExp(/[-+()\s]/, 'ig');

	const formattedPhoneNumber = phoneNumber.replace(phoneFormatPattern, '');

	return formattedPhoneNumber;
};

interface ILoadingState {
	user: boolean;
	login: boolean;
	registration: boolean;
	requestPassword: boolean;
}

export interface IRegistrationUserParams {
	name: string;
	phone: string;
}

export interface ILoginParams {
	password: string;
	username: string;
}

export interface IUserState {
	user: null | IUser;
	loading: ILoadingState;
	error: unknown;

	isLoadingUser: boolean;
	isLoadedSuccess: boolean;
}

export const MutationType = {
	SET_USER: 'setUser',
	SET_LOADING: 'setPageLoadin',
	SET_ERROR: 'setError',

	SET_LOADING_USER: 'setLoadingUser',
	SET_LOADED_SUCCESS: 'setLoadedSuccess',
};

export const actionType = {
	REQUEST_PASSWORD: 'requestPassword',
	GET_USER: 'getUser',
	LOGIN: 'login',
	LOGOUT: 'logout',
	REGISTER: 'register',
};

export default function userStore(): Module<IUserState, any> {
	return {
		namespaced: true,
		state: () => ({
			user: null,
			loading: {
				user: false,
				login: false,
				registration: false,
				requestPassword: false,
			},
			error: null,

			isLoadingUser: true,
			isLoadedSuccess: false,
		}),
		mutations: {
			[MutationType.SET_USER]: (state, user: null | IUser) => {
				state.user = user;
			},
			[MutationType.SET_LOADING]: (state, { key, value }: { key: keyof ILoadingState; value: boolean }) => {
				state.loading[key] = value;
			},
			[MutationType.SET_ERROR]: (state, err: unknown) => {
				state.error = err;
			},

			[MutationType.SET_LOADING_USER]: (state, val: boolean) => {
				state.isLoadingUser = val;
			},
			[MutationType.SET_LOADED_SUCCESS]: (state, val: boolean) => {
				state.isLoadedSuccess = val;
			},
		},
		actions: {
			async [actionType.REGISTER]({ commit }, params: IRegistrationUserParams) {
				try {
					commit(MutationType.SET_LOADING, { key: 'registration', value: true });
					commit('appState/setLoading', { registration: true }, { root: true });
					commit(MutationType.SET_ERROR, null);

					console.log('register', {
						name: params.name,
						phone: formatPhoneNumber(params.phone),
					});

					const res = await this.$api.user.signUp.execute({
						name: params.name,
						phone: formatPhoneNumber(params.phone),
					});
					if (res.isFailure) {
						const e = res.getError();
						commit(MutationType.SET_ERROR, e?.message);
					}
					return res.isSuccess;
				} catch (e) {
					commit(MutationType.SET_ERROR, e);
					return false;
				} finally {
					commit(MutationType.SET_LOADING, { key: 'registration', value: false });
					commit('appState/setLoading', { registration: false }, { root: true });
				}
			},
			async [actionType.GET_USER]({ commit }, params = {}): Promise<IUser | boolean> {
				try {
					commit('appState/setLoading', { user: true }, { root: true });
					const res = await this.$api.user.getUser.execute(params || {});
					if (res.isSuccess) {
						const value = res.getValue();
						commit(MutationType.SET_USER, value?.user);
						commit(MutationType.SET_LOADING_USER, false);
						commit(MutationType.SET_LOADED_SUCCESS, true);
						return value?.user;
					} else {
						commit(MutationType.SET_LOADING_USER, false);
						commit(MutationType.SET_LOADED_SUCCESS, false);
					}
					return false;
				} catch (e) {
					commit(MutationType.SET_ERROR, e);
					commit(MutationType.SET_LOADING_USER, false);
					commit(MutationType.SET_LOADED_SUCCESS, false);
					return false;
				} finally {
					commit('appState/setLoading', { user: false }, { root: true });
				}
			},
			async [actionType.REQUEST_PASSWORD]({ commit }, phoneNumber: string): Promise<boolean | undefined> {
				try {
					commit(MutationType.SET_LOADING, { key: 'requestPassword', value: true });
					commit('appState/setLoading', { requestPassword: true }, { root: true });

					const res = await this.$api.user.requestPassword.execute({
						phone: formatPhoneNumber(phoneNumber),
					});
					if (res.isSuccess) {
						const ts = Date.now();
						localStorage.setItem(PASS_REQUEST_TIMESTAMP, String(ts));
					}
					return res.isSuccess;
				} catch (e) {
					commit(MutationType.SET_ERROR, e);
					return false;
				} finally {
					commit(MutationType.SET_LOADING, { key: 'requestPassword', value: false });
					commit('appState/setLoading', { requestPassword: false }, { root: true });
				}
			},
			[actionType.LOGOUT]({ commit, dispatch }): void {
				dispatch('userAuth/setToken', { authToken: '' }, { root: true });
				this.$cookies.remove(USER_JWT_TOKEN);
				this.$cookies.remove(USER_SESSION_TOKEN);
				commit(MutationType.SET_USER, null);
				dispatch('userAuth/UNSET_TOKENS', null, { root: true });
			},
			async [actionType.LOGIN](
				{ commit },
				params: ILoginParams,
			): Promise<{ sessionToken: string; authToken: string } | boolean | undefined> {
				try {
					commit(MutationType.SET_LOADING, { key: 'login', value: true });
					commit('appState/setLoading', { login: true }, { root: true });

					const reqBody = {
						username: formatPhoneNumber(params.username),
						password: params.password,
					};
					const res = await this.$api.user.signIn.execute(reqBody);
					if (res.isSuccess) {
						localStorage.removeItem(PASS_REQUEST_TIMESTAMP);
						const value = res.getValue();
						if (value?.token) {
							this.$cookies.set(USER_JWT_TOKEN, value.token, {
								expires: new Date(new Date().setFullYear(new Date().getFullYear() + 5))
							});
							return {
								authToken: value.token,
								sessionToken: value.sessionToken,
							};
						}
					}
					return res.isSuccess;
				} catch (e) {
					commit(MutationType.SET_ERROR, e);
					return false;
				} finally {
					commit(MutationType.SET_LOADING, { key: 'login', value: false });
					commit('appState/setLoading', { login: false }, { root: true });
				}
			},
		},
		getters: {
			user: ({ user }) => user,
			isLoggedIn: ({ user }) => Boolean(user && user.id),
			loading: ({ loading }) => loading,
			userError: ({ error }) => error,
			isLoadingUser: ({ isLoadingUser }) => isLoadingUser,
			isLoadedSuccess: ({ isLoadedSuccess }) => isLoadedSuccess,
		},
	};
}
